import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsToggle } from './shared/SettingsToggle';
import { Bell, Clock, MessageCircle, Gift, Image, DollarSign, Award, Volume2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
interface NotificationSectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const NotificationSection: React.FC<NotificationSectionProps> = ({
  setHasUnsavedChanges
}) => {
  const [notifications, setNotifications] = useState({
    push: {
      enabled: true,
      messages: true,
      gifts: true,
      newContent: true,
      promotions: false,
      deviceList: [{
        id: 1,
        name: 'iPhone 13',
        lastActive: 'Now'
      }, {
        id: 2,
        name: 'Chrome on Windows',
        lastActive: '2 hours ago'
      }]
    },
    email: {
      enabled: true,
      messages: true,
      gifts: true,
      newContent: true,
      promotions: true,
      frequency: 'daily' // daily, weekly, immediate
    },
    site: {
      enabled: true,
      messages: true,
      gifts: true,
      newContent: true,
      promotions: false
    },
    toast: {
      enabled: true,
      messages: true,
      gifts: true,
      newContent: false,
      promotions: false
    },
    other: {
      autoMuteInactive: true,
      notifyPremiumUnlock: true,
      notifyXpMilestone: true,
      sounds: true
    }
  });
  const handleToggleChange = (category: string, field: string, value: boolean) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };
  const handleFrequencyChange = (value: string) => {
    setNotifications(prev => ({
      ...prev,
      email: {
        ...prev.email,
        frequency: value
      }
    }));
    setHasUnsavedChanges(true);
  };
  const handleRemoveDevice = (id: number) => {
    setNotifications(prev => ({
      ...prev,
      push: {
        ...prev.push,
        deviceList: prev.push.deviceList.filter(device => device.id !== id)
      }
    }));
    setHasUnsavedChanges(true);
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18667">
      <SettingsCard title="Notification Preferences" description="Control how and when you receive notifications across different channels." data-id="element-18668">
        <Tabs defaultValue="push" data-id="element-18669">
          <TabsList className="mb-4" data-id="element-18670">
            <TabsTrigger value="push" data-id="element-18671">Push</TabsTrigger>
            <TabsTrigger value="email" data-id="element-18672">Email</TabsTrigger>
            <TabsTrigger value="site" data-id="element-18673">Site</TabsTrigger>
            <TabsTrigger value="toast" data-id="element-18674">Toast</TabsTrigger>
          </TabsList>
          <TabsContent value="push" data-id="element-18675">
            <div className="space-y-4" data-id="element-18676">
              <SettingsToggle id="push-enabled" label="Push Notifications" description="Receive notifications on your devices even when you're not on SinShift." checked={notifications.push.enabled} onChange={checked => handleToggleChange('push', 'enabled', checked)} data-id="element-18677" />
              {notifications.push.enabled && <>
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-4 pt-2" data-id="element-18678">
                    <SettingsToggle id="push-messages" label="Messages" description="Get notified when you receive new messages." checked={notifications.push.messages} onChange={checked => handleToggleChange('push', 'messages', checked)} data-id="element-18679" />
                    <SettingsToggle id="push-gifts" label="Gifts & Tips" description="Get notified when you receive gifts or tips." checked={notifications.push.gifts} onChange={checked => handleToggleChange('push', 'gifts', checked)} data-id="element-18680" />
                    <SettingsToggle id="push-content" label="New Content" description="Get notified when angels you follow post new content." checked={notifications.push.newContent} onChange={checked => handleToggleChange('push', 'newContent', checked)} data-id="element-18681" />
                    <SettingsToggle id="push-promotions" label="Promotions & Offers" description="Get notified about special offers and promotions." checked={notifications.push.promotions} onChange={checked => handleToggleChange('push', 'promotions', checked)} data-id="element-18682" />
                  </div>
                  <div className="mt-6" data-id="element-18683">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-18684">
                      Registered Devices
                    </h4>
                    {notifications.push.deviceList.length > 0 ? <div className="space-y-3" data-id="element-18685">
                        {notifications.push.deviceList.map(device => <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-18686">
                            <div data-id="element-18687">
                              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18688">
                                {device.name}
                              </h5>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18689">
                                Last active: {device.lastActive}
                              </p>
                            </div>
                            <button className="px-3 py-1.5 bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-xs" onClick={() => handleRemoveDevice(device.id)} data-id="element-18690">
                              Remove
                            </button>
                          </div>)}
                      </div> : <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-id="element-18691">
                        No devices registered for push notifications.
                      </p>}
                  </div>
                </>}
            </div>
          </TabsContent>
          <TabsContent value="email" data-id="element-18692">
            <div className="space-y-4" data-id="element-18693">
              <SettingsToggle id="email-enabled" label="Email Notifications" description="Receive notifications via email." checked={notifications.email.enabled} onChange={checked => handleToggleChange('email', 'enabled', checked)} data-id="element-18694" />
              {notifications.email.enabled && <>
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-4 pt-2" data-id="element-18695">
                    <SettingsToggle id="email-messages" label="Messages" description="Get notified when you receive new messages." checked={notifications.email.messages} onChange={checked => handleToggleChange('email', 'messages', checked)} data-id="element-18696" />
                    <SettingsToggle id="email-gifts" label="Gifts & Tips" description="Get notified when you receive gifts or tips." checked={notifications.email.gifts} onChange={checked => handleToggleChange('email', 'gifts', checked)} data-id="element-18697" />
                    <SettingsToggle id="email-content" label="New Content" description="Get notified when angels you follow post new content." checked={notifications.email.newContent} onChange={checked => handleToggleChange('email', 'newContent', checked)} data-id="element-18698" />
                    <SettingsToggle id="email-promotions" label="Promotions & Offers" description="Get notified about special offers and promotions." checked={notifications.email.promotions} onChange={checked => handleToggleChange('email', 'promotions', checked)} data-id="element-18699" />
                  </div>
                  <div className="mt-4" data-id="element-18700">
                    <label htmlFor="email-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-18701">
                      Email Frequency
                    </label>
                    <select id="email-frequency" value={notifications.email.frequency} onChange={e => handleFrequencyChange(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18702">
                      <option value="immediate" data-id="element-18703">Immediate</option>
                      <option value="daily" data-id="element-18704">Daily Digest</option>
                      <option value="weekly" data-id="element-18705">Weekly Digest</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18706">
                      Choose how often you want to receive email notifications.
                    </p>
                  </div>
                </>}
            </div>
          </TabsContent>
          <TabsContent value="site" data-id="element-18707">
            <div className="space-y-4" data-id="element-18708">
              <SettingsToggle id="site-enabled" label="Site Notifications" description="Receive notifications within the SinShift website." checked={notifications.site.enabled} onChange={checked => handleToggleChange('site', 'enabled', checked)} data-id="element-18709" />
              {notifications.site.enabled && <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-4 pt-2" data-id="element-18710">
                  <SettingsToggle id="site-messages" label="Messages" description="Get notified when you receive new messages." checked={notifications.site.messages} onChange={checked => handleToggleChange('site', 'messages', checked)} data-id="element-18711" />
                  <SettingsToggle id="site-gifts" label="Gifts & Tips" description="Get notified when you receive gifts or tips." checked={notifications.site.gifts} onChange={checked => handleToggleChange('site', 'gifts', checked)} data-id="element-18712" />
                  <SettingsToggle id="site-content" label="New Content" description="Get notified when angels you follow post new content." checked={notifications.site.newContent} onChange={checked => handleToggleChange('site', 'newContent', checked)} data-id="element-18713" />
                  <SettingsToggle id="site-promotions" label="Promotions & Offers" description="Get notified about special offers and promotions." checked={notifications.site.promotions} onChange={checked => handleToggleChange('site', 'promotions', checked)} data-id="element-18714" />
                </div>}
            </div>
          </TabsContent>
          <TabsContent value="toast" data-id="element-18715">
            <div className="space-y-4" data-id="element-18716">
              <SettingsToggle id="toast-enabled" label="Toast Notifications" description="Receive pop-up notifications while browsing SinShift." checked={notifications.toast.enabled} onChange={checked => handleToggleChange('toast', 'enabled', checked)} data-id="element-18717" />
              {notifications.toast.enabled && <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-4 pt-2" data-id="element-18718">
                  <SettingsToggle id="toast-messages" label="Messages" description="Get pop-up notifications for new messages." checked={notifications.toast.messages} onChange={checked => handleToggleChange('toast', 'messages', checked)} data-id="element-18719" />
                  <SettingsToggle id="toast-gifts" label="Gifts & Tips" description="Get pop-up notifications for gifts and tips." checked={notifications.toast.gifts} onChange={checked => handleToggleChange('toast', 'gifts', checked)} data-id="element-18720" />
                  <SettingsToggle id="toast-content" label="New Content" description="Get pop-up notifications for new content." checked={notifications.toast.newContent} onChange={checked => handleToggleChange('toast', 'newContent', checked)} data-id="element-18721" />
                  <SettingsToggle id="toast-promotions" label="Promotions & Offers" description="Get pop-up notifications for promotions and offers." checked={notifications.toast.promotions} onChange={checked => handleToggleChange('toast', 'promotions', checked)} data-id="element-18722" />
                </div>}
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mt-4" data-id="element-18723">
                <div className="flex items-center gap-3" data-id="element-18724">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900/30" data-id="element-18725">
                    <Bell size={20} className="text-pink-500" data-id="element-18726" />
                  </div>
                  <div data-id="element-18727">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18728">
                      Toast Notification Preview
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18729">
                      This is how toast notifications will appear on your
                      screen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </SettingsCard>
      <SettingsCard title="Other Notification Settings" description="Additional notification preferences for your SinShift experience." data-id="element-18730">
        <div className="space-y-4" data-id="element-18731">
          <SettingsToggle id="auto-mute" label="Auto-mute inactive fans" description="Automatically mute notifications from fans who haven't interacted with you in 30 days." checked={notifications.other.autoMuteInactive} onChange={checked => handleToggleChange('other', 'autoMuteInactive', checked)} data-id="element-18732" />
          <SettingsToggle id="premium-unlock" label="Notify on premium unlock" description="Get notified when someone unlocks your premium content." checked={notifications.other.notifyPremiumUnlock} onChange={checked => handleToggleChange('other', 'notifyPremiumUnlock', checked)} data-id="element-18733" />
          <SettingsToggle id="xp-milestone" label="Notify on XP milestone" description="Get notified when you reach an XP milestone with a fan." checked={notifications.other.notifyXpMilestone} onChange={checked => handleToggleChange('other', 'notifyXpMilestone', checked)} data-id="element-18734" />
          <SettingsToggle id="notification-sounds" label="Notification Sounds" description="Play sounds when you receive notifications." checked={notifications.other.sounds} onChange={checked => handleToggleChange('other', 'sounds', checked)} data-id="element-18735" />
        </div>
      </SettingsCard>
    </div>;
};